import { allocationAPI } from 'api/AllocationAPI'
import { useEffect, useState } from 'react'
import { IError } from 'types/error'
import { IAllocation } from 'types/assignmentInterfaces'

/**
 * Hook for getting allocations for a person.
 *
 * @param personId - ID of the person to get allocations for.
 * @returns Hook variables and functions.
 */
export const useAllocations = (personId: number) => {
  const [allocations, setAllocations] = useState<IAllocation[]>([])
  const [error, setError] = useState<IError>()
  const [update, setUpdate] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        setLoading(true)
        const allocations = await allocationAPI.getAllocations(personId, controller)
        setAllocations(allocations)
      } catch (error) {
        setError(error as IError)
      } finally {
        setUpdate(false)
        setLoading(false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [personId, update])

  return {
    allocations,
    error,
    setUpdate,
    loading,
  }
}

/**
 * Hook for getting allocations for all people allocated to users organizations owned assignments.
 *
 * @param salesAccess - Whether the user has sales access or not.
 * @returns Hook variables and functions.
 */
export const useSupplierAllocations = (salesAccess: boolean) => {
  const [allocations, setAllocations] = useState<IAllocation[]>([])
  const [error, setError] = useState<IError>()
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    const controller = new AbortController()

    if (salesAccess) {
      ;(async () => {
        try {
          setLoading(true)
          const items = await allocationAPI.getSupplierAllocations(controller)
          setAllocations(items)
        } catch (error) {
          setError(error as IError)
        } finally {
          setLoading(false)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [salesAccess])

  return {
    allocations,
    error,
    loading,
  }
}
